import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { BaseError } from '@utils/types/shared/base';
import { OnPremUsageReportResponse } from '@utils/types/back-office/on-prem-billing';

/**
 * Hook to fetch on-prem usage report for a specific organization
 *
 * GET /api/admin/billing/on-prem/{org_id}/report
 * Query params:
 *   - start_date (format: YYYY-MM-DD)
 *   - end_date (format: YYYY-MM-DD)
 *
 * Returns usage report with STT units, alignment units, translation characters,
 * models trained/hosted, and estimated costs
 */
export const useGetOnPremUsageReport = (
  orgId: number | string | undefined,
  startDate: string | undefined,
  endDate: string | undefined
) => {
  const fetchReport = async () => {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }

    const queryString = params.toString();
    const url = `${URL_EXTENSION}/admin/billing/on-prem/${orgId}/report${queryString ? `?${queryString}` : ''}`;

    const response = await Axios.get<OnPremUsageReportResponse>(url);
    return response.data;
  };

  return useQuery<OnPremUsageReportResponse, AxiosError<BaseError>>(
    ['onPremUsageReport', orgId, startDate, endDate],
    fetchReport,
    {
      enabled: !!orgId && !!startDate && !!endDate,
      useErrorBoundary: false
    }
  );
};
